import React, { useMemo, useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import compact from 'lodash/compact';
import { useStaticQuery, graphql, Link } from 'gatsby';
import ReactSelect, { components } from 'react-select';
import { useIntl } from 'react-intl';

import { addTrailingSlash } from '../../lib/helpers';
import dropdownIndicator from '../../MOVEMETOCDN/dropdown-indicator.svg';
import globe from '../../MOVEMETOCDN/globe.svg';
import NavigationBrand from '../NavigationBrand/NavigationBrand';
import TopBarCTA from '../TopBarCTA/TopBarCTA';
import { NavigationItem } from '../NavigationItem/NavigationItem';
import { internationalLangSelectorStyles, productListStyles } from './react-select-styles';
import { WebsiteLocaleFragment } from '../../../graphql.schema';
import { GlobalPageContext } from '../../types';
import bcp47LanguageCodes from './bcp47LanguageCode.json';
import localeDropDownArrow from '../../MOVEMETOCDN/web-arrow-white.svg';
import { v4tov3base } from '../../lib/mapV4toV3';
import { PromotionCard } from '../PromotionCard';
import { NavigationMobile } from '../NavigationMobile/NavigationMobile';

import * as styles from './Header.module.scss';

type HeaderProps = {
  websiteLocale: WebsiteLocaleFragment;
  isInternationalSite?: boolean;
  pathPrefixes: GlobalPageContext['pathPrefixes'];
  id: string;
  availableRoutes: any
};

type WebsiteLocaleTypes = {
  documentId: string;
  website_locales: Array<{
    language_code: string;
    is_default: boolean
  }>;
  default_website_locale: {
    language_code: string;
  };
};

const LoginIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M11.5 8C12.3284 8 13 8.67157 13 9.5V10C13 11.9714 11.1405 14 8 14C4.85951 14 3 11.9714 3 10V9.5C3 8.67157 3.67157 8 4.5 8H11.5ZM11.5 9H4.5C4.22386 9 4 9.22386 4 9.5V10C4 11.4376 5.43216 13 8 13C10.5678 13 12 11.4376 12 10V9.5C12 9.22386 11.7761 9 11.5 9ZM8 1.5C9.51878 1.5 10.75 2.73122 10.75 4.25C10.75 5.76878 9.51878 7 8 7C6.48122 7 5.25 5.76878 5.25 4.25C5.25 2.73122 6.48122 1.5 8 1.5ZM8 2.5C7.0335 2.5 6.25 3.2835 6.25 4.25C6.25 5.2165 7.0335 6 8 6C8.9665 6 9.75 5.2165 9.75 4.25C9.75 3.2835 8.9665 2.5 8 2.5Z"
      fill="#CDDEFF"
      stroke="#CDDEFF"
      strokeWidth="0.5"
    />
  </svg>
)

const DropdownIndicator = (props: any) => (
  <components.DropdownIndicator {...props}>
    <LoginIcon />
  </components.DropdownIndicator>
)
const Header: React.FC<HeaderProps> = ({ websiteLocale, isInternationalSite = false, pathPrefixes, id, availableRoutes }) => {
  const learningProducts = websiteLocale?.learningProducts;
  const [isOpen, setIsOpen] = useState(false);
  const [isShowContainer, setContainerStatus] = useState(false);
  const [isNavigationVisible, setIsNavigationVisible] = useState(true);
  const lastScrollYRef = useRef(0);
  const intl = useIntl();
  const [hasMounted, setHasMounted] = useState(false);
  
  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerWidth < 768) return;

      const currentScrollY = window.scrollY;

      if (currentScrollY === 0) {
        // Always visible at top
        setIsNavigationVisible(true);
      } else if (currentScrollY < lastScrollYRef.current) {
        // Scrolling up - show navigation
        setIsNavigationVisible(true);
      } else if (currentScrollY > lastScrollYRef.current && currentScrollY > 60) {
        // Scrolling down and past 60px - hide navigation
        setIsNavigationVisible(false);
      }
      
      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOpen]);
  
  const {
    site: { siteMetadata },
    strapi: v4Strapi,
  } = useStaticQuery(graphql`
    query StaticHeaderData {
      site {
        siteMetadata {
          title
          studentLogin
        }
      }
      strapi {
        websites(pagination: { limit: 100}) {
          data {
            documentId
            attributes {
              website_locales {
                data {
                  documentId
                  attributes {
                    learningProducts: learning_products {
                      productName: product_name
                      productLinkUrl: product_link_url
                      productImage: product_image {
                        data {
                          documentId
                          attributes {
                            url
                          }
                        }
                      }
                    }
                    language_code
                     is_default
                    localizations{
                      data{
                      attributes{
                       locale
                       is_default
                      }
                     }
                     }
                     show_countdown_in_promotion
                  }
                }
              }
              default_website_locale {
                data {
                  attributes {
                    language_code
                    is_default
                    localizations{
                      data{
                      attributes{
                       locale
                        is_default
                      }
                     }
                     }
                  }
                }
              }
            }
          }
        }
        homepages(pagination: { limit: 100 }){
          data {
            attributes {
              locale
              publishedAt
              website_locale {
                data {
                  documentId
                }
              }
            }
          }
        }
      }
    }
  `);

  const strapi = v4tov3base(v4Strapi);
  const websiteLocaleToV3 = {
    id: websiteLocale.documentId,
    // @ts-ignore
    ...v4tov3base(websiteLocale),
  };
  const websites = strapi.websites;
  const homepages = strapi.homepages;
  const navigationItems = compact(websiteLocaleToV3.navigation || []);
  const mapHeaderSiteLinks = (siteLinks: WebsiteLocaleFragment['internationalHeaderSiteLinks'], region: string) => {
    return compact(
      compact(siteLinks)
        .filter((link) => link.region === region)
        .map(({ linkUrl, territory, flagImage }) => {
          if (linkUrl && territory && flagImage) {
            return {
              value: territory,
              label: `
                <a class="${styles.locationDropdownLabel}" href="${linkUrl}">
                  <img
                    class="${styles.locationDropdownIcon}"
                    src="${flagImage?.data.attributes.url}"
                    alt="${intl.formatMessage({ id: 'flagOfCountry' }, { country: territory })}"
                    loading="lazy"
                  />
                  ${territory}
                </a>`,
            };
          }
          return null;
        }),
    );
  };

  const groupedOptions = [
    {
      label: 'Asia Pacific',
      options: mapHeaderSiteLinks(websiteLocale.internationalHeaderSiteLinks, 'asia_pacific'),
    },
    {
      label: 'Europe, middle-east & africa',
      options: mapHeaderSiteLinks(websiteLocale.internationalHeaderSiteLinks, 'europe_africa_and_middle_east'),
    },
    {
      label: 'Latin America',
      options: mapHeaderSiteLinks(websiteLocale.internationalHeaderSiteLinks, 'latin_america'),
    },
  ];

  const Options = learningProducts.map(({ productName, productLinkUrl, productImage }) => {
    let dropdownLabel;

    if (productImage?.data == null || undefined) {
      dropdownLabel = `
      <span class="${styles.productDropdownContent}">${productName}</span>`;
    } else {
      dropdownLabel = `<img
        class="${styles.productDropdownIcon}"
        src="${productImage?.data?.attributes?.url}"
        alt="${productName}"
        loading="lazy"
      />`;
    }

    return {
      label: ` 
        <a class="${styles.locationDropdownLabel}" href="${productLinkUrl}" target="_blank">
          ${dropdownLabel}
        </a>`,
    };
  });

  const Placeholder = (props: any) => {
    return (
      <components.Placeholder {...props}>
        <div className={styles.langSelectorContainer}>
          <svg className='is-hidden-desktop' width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M8 0C3.58161 0 0 3.58161 0 8C0 12.4184 3.58161 16 8 16C12.4184 16 16 12.4184 16 8C16 3.58161 12.4184 0 8 0ZM10.6545 11.5355C10.5287 11.6606 10.3968 11.7919 10.2897 11.8994C10.1932 11.9961 10.1252 12.1155 10.0906 12.2448C10.0419 12.4274 10.0026 12.6119 9.93677 12.789L9.37581 14.3003C8.93194 14.3971 8.47258 14.4516 8 14.4516V13.5684C8.05452 13.1613 7.75355 12.3987 7.27 11.9152C7.07645 11.7216 6.96774 11.459 6.96774 11.1852V10.1526C6.96774 9.7771 6.76548 9.43194 6.43677 9.25032C5.97323 8.99387 5.31387 8.63548 4.86226 8.40806C4.49194 8.22161 4.14935 7.98419 3.84129 7.70645L3.81548 7.68323C3.59518 7.48437 3.39958 7.25974 3.2329 7.01419C2.93032 6.57 2.43742 5.83935 2.1171 5.36452C2.77742 3.89677 3.96742 2.71806 5.44613 2.07774L6.22064 2.46516C6.56387 2.63677 6.96774 2.38742 6.96774 2.00355V1.63903C7.22548 1.59742 7.48774 1.57097 7.75452 1.56097L8.66742 2.47387C8.86903 2.67548 8.86903 3.00226 8.66742 3.20387L8.51613 3.35484L8.18258 3.68839C8.08193 3.78903 8.08193 3.95258 8.18258 4.05323L8.33387 4.20452C8.43452 4.30516 8.43452 4.46871 8.33387 4.56935L8.07581 4.82742C8.02735 4.87578 7.96168 4.90293 7.89323 4.9029H7.60323C7.53613 4.9029 7.47161 4.92903 7.42323 4.97613L7.10323 5.28742C7.064 5.32562 7.03793 5.37529 7.02876 5.42927C7.0196 5.48325 7.02783 5.53874 7.05226 5.58774L7.55516 6.59387C7.64097 6.76548 7.51613 6.96742 7.32452 6.96742H7.14258C7.08032 6.96742 7.02032 6.94484 6.97355 6.90419L6.67419 6.64419C6.60645 6.58543 6.52458 6.5453 6.43663 6.52777C6.34868 6.51023 6.25768 6.5159 6.17258 6.54419L5.1671 6.87935C5.09033 6.90495 5.02357 6.95406 4.97627 7.01971C4.92896 7.08537 4.90352 7.16424 4.90355 7.24516C4.90355 7.39129 4.98613 7.52452 5.11677 7.59L5.47419 7.76871C5.77774 7.92064 6.11258 7.99968 6.45194 7.99968C6.79129 7.99968 7.18064 8.88 7.48419 9.03193H9.63742C9.91129 9.03193 10.1735 9.14064 10.3674 9.33419L10.809 9.77581C10.9935 9.96037 11.0971 10.2107 11.0971 10.4716C11.097 10.6694 11.0579 10.8651 10.9819 11.0477C10.906 11.2303 10.7947 11.396 10.6545 11.5355ZM13.4516 8.58871C13.2648 8.54193 13.1019 8.42742 12.9952 8.2671L12.4152 7.3971C12.3303 7.26999 12.285 7.12058 12.285 6.96774C12.285 6.8149 12.3303 6.6655 12.4152 6.53839L13.0471 5.59064C13.1219 5.47871 13.2245 5.38774 13.3452 5.32774L13.7639 5.11839C14.2 5.98677 14.4516 6.96355 14.4516 8C14.4516 8.27968 14.4277 8.55355 14.3929 8.82387L13.4516 8.58871Z"
              fill="#CDDEFF"
              fillOpacity="0.35"
            />
          </svg>
          <span>Global</span>
        </div>
      </components.Placeholder>
    );
  };

  const LocalesDropDown: JSX.Element | null = useMemo(() => {
    const locales: WebsiteLocaleTypes = websites.find((e: WebsiteLocaleTypes) => e.documentId === id);
    if (!locales) {
      return null;
    }

    const availableWebsiteLocales = locales.website_locales.reduce((accumulator, e) => {
      const availableHomepages = homepages.filter((item) => item.website_locale.documentId === e.documentId && item.publishedAt != null);
      const [language, country] = e.language_code.split('-');
      const languageName = bcp47LanguageCodes[language]?.nativeName;
      const route = compact(availableRoutes).find((x) => x?.locale == e.language_code ? x?.route : null);
      const mainRoute = `${global.window?.origin ?? ''}/${route?.route}`;
      if (availableHomepages.length > 0) {
        const localeData = {
          value: language.toUpperCase(),
          label: languageName ? languageName : '',
          locale: e.language_code,
          redirectUri: e.is_default ? mainRoute.replace(new RegExp(`/${language}`), '') : mainRoute,
          route: route?.route
        };
        accumulator.push(localeData);
      }
      return accumulator;
    }, [])

    return availableWebsiteLocales.length > 1 ? (
      <div className={classnames(styles.languageDropdown)} onClick={() => setContainerStatus(!isShowContainer)}>
        <div className={classnames("is-flex", websiteLocale.hasRightToLeftLanguage ? styles.languageCode : '')}>
          {availableWebsiteLocales.find((e) => e.locale === websiteLocale.languageCode)?.label ?? ''}
          <img src={localeDropDownArrow} className={styles.invertImage} />
        </div>
        <div
          className={classnames(
            styles.languageContent,
            isShowContainer ? styles.languageContentDisplay : styles.languageContentHide,
          )}
        >
          {availableWebsiteLocales
            .filter((e) => e.locale !== websiteLocale.languageCode)
            .map((data) => (
              <div key={data.value}>
                <div className={classnames(data.route ? '' : styles.wrapper)}>
                  <Link to={data.route ? addTrailingSlash(data.redirectUri) : ''} key={data.value} className={classnames('is-flex', styles.languageOption, data.route ? styles.routeAvailable : styles.routeNotAvailable)}>
                    <p >{data.label}</p>
                  </Link>
                </div>
              </div>
            ))
            }
        </div>
      </div>
    ) : null;
  }, [websiteLocale, isShowContainer]);
  return (

    <header className={classnames(styles.root)}>
      <div className={classnames('has-bg-brand-a py-2', styles.overhead)}>
        <div className={classnames('container', styles.overheadInner, { [styles.international]: isInternationalSite })}>
          {websiteLocaleToV3?.promotion?.slug ?
            <div className={styles.promotionContainer}>
              <PromotionCard
                title={websiteLocaleToV3?.promotion?.title}
                sub_title={websiteLocaleToV3?.promotion?.homepage_link_text}
                expires_at={websiteLocaleToV3?.promotion?.expires_at ?? null}
                showCountdown={websiteLocaleToV3?.show_countdown_in_promotion}
                href={`${pathPrefixes.promotions}/${websiteLocaleToV3?.promotion?.slug}`}
              />
            </div>
          : null}

          <div className={classnames(styles.topBarLinks, { [styles.international]: isInternationalSite })}>
            <div className={styles.topBarCta}>
              <TopBarCTA websiteLocale={websiteLocale} cta={websiteLocaleToV3.topBarCta} />
            </div>
            {websiteLocaleToV3.telephone ? (
              <a
                className={classnames(styles.telephoneLink, 'is-hidden-mobile')}
                href={`tel:${websiteLocaleToV3.telephone}`}
                data-testid='telephone'
              >
                {websiteLocaleToV3.telephone}
              </a>
            ) : null}
            {!isInternationalSite && <div className="is-hidden-mobile is-flex pl-3" data-testid='localedropdown'>{LocalesDropDown}</div>}
            {(id === 'o1vsas7qxp9yt1yx5o1maxw2' || id === 'frv0glxlt30v75jyrrbg28q5') ?
              <div data-testid='login-dropdown'>
                {hasMounted ? (
                  <ReactSelect
                    styles={productListStyles}
                    instanceId="login-dropdown"
                    options={Options}
                    value={undefined}
                    components={{ IndicatorSeparator: () => null, DropdownIndicator }}
                    onChange={() => null}
                    hideSelectedOptions={false}
                    isSearchable={false}
                    formatOptionLabel={(data) => <span dangerouslySetInnerHTML={{ __html: data.label }}></span>}
                    placeholder={intl.formatMessage({ id: 'login-text', defaultMessage: 'Login' })}
                    controlShouldRenderValue={false}
                  />
                ) : null}
              </div>
              :
              <a href={siteMetadata.studentLogin} target="_blank" className={classnames('link', styles.studentLoginLink)} rel="nofollow" data-testid='student-login'>
                {intl.formatMessage({ id: 'login-text', defaultMessage: 'Student Login' })}
                <LoginIcon />
              </a>
            }
            {isInternationalSite ? (
              <div data-testid='select-country' className={styles.locationDropdown}>
                <div>
                  {hasMounted ? (
                    <ReactSelect
                      styles={internationalLangSelectorStyles}
                      options={groupedOptions}
                      instanceId="territory-dropdown"
                      value={undefined}
                      components={{ IndicatorSeparator: () => null, Placeholder }}
                      onChange={() => null}
                      hideSelectedOptions={false}
                      isSearchable={false}
                      formatOptionLabel={(data) => <span dangerouslySetInnerHTML={{ __html: data.label }}></span>}
                    />
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className={classnames(styles.headerContainer, { [styles.isNavigationVisible]: isNavigationVisible })}>
        <div className={classnames('container', styles.mobileContainer)}>
          <nav
            className={classnames(styles.navigation, 'navigation navbar is-white')}
            role="navigation"
            aria-label="main navigation"
          >
            <NavigationBrand
              isOpen={isOpen}
              onMenuToggle={setIsOpen}
              pathPrefixes={pathPrefixes}
              telephoneNumber={websiteLocaleToV3.telephone ?? ''}
              LocalesDropDown={LocalesDropDown}
              websiteLocale={websiteLocale}
              websiteLocaleToV3={websiteLocaleToV3}
            />

            <div className={classnames('navbar-menu is-hidden-mobile', styles.navbarMenu, { 'is-active': isOpen })}>
              <div className="navbar-end mr-3">
                {hasMounted && navigationItems.map((item, idx) => {
                  if (!item) return null;
                  return (
                    <NavigationItem
                      data-testid='nav-dropdown'
                      key={`header-navitem-${idx}`}
                      item={item}
                      className={classnames({ 'is-right': idx === navigationItems.length - 1 })}
                    />
                  );
                })}
                {websiteLocaleToV3.externalBlogText && websiteLocaleToV3.externalBlogUrl && (
                  <a
                    className="navbar-item is-text-1"
                    target="_blank"
                    rel="noreferrer"
                    href={websiteLocaleToV3.externalBlogUrl}
                    data-testid='navbar-item'
                  >
                    {websiteLocaleToV3.externalBlogText}
                  </a>
                )}
              </div>
            </div>

            <div className={classnames('navbar-menu is-hidden-tablet', styles.navbarMenu, { 'is-active': isOpen })}>
              {hasMounted ? <NavigationMobile navigationItems={navigationItems} isOpen={isOpen} /> : null}
            </div>
          </nav>

          {websiteLocaleToV3?.promotion?.slug ?
            <div className={"is-hidden-desktop"}>
              <PromotionCard
                title={websiteLocaleToV3?.promotion?.title}
                sub_title={websiteLocaleToV3?.promotion?.homepage_link_text}
                expires_at={websiteLocaleToV3?.promotion?.expires_at ?? null}
                href={`${pathPrefixes.promotions}/${websiteLocaleToV3?.promotion?.slug}`}
                mobile={true}
              />
            </div>
          : null}
        </div>
      </div>
    </header>
  );
};

export default Header;